import banner from "../../../courses/module-2/img2.png";

const modulo2_pt = {
  courseId: "cripto-blockchain",
  id: "modulo-2",
  title: "🧩 MÓDULO 2: Blockchain para iniciantes",
  estimatedDuration: "1 hora e 30 minutos",
  format: "Leitura + atividade de verificação",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivo do módulo",
      content: [
        "Entender o que é blockchain (cadeia de blocos), como as transações são validadas dentro dessa tecnologia e por que esse sistema é a base de funcionamento da Web3.",
      ],
    },
    {
      id: "definicion",
      navLabel: "O que é blockchain?",
      title: "🔍 O que é uma blockchain (cadeia de blocos)?",
      content: [
        {
          type: "paragraph",
          text: "🧠 Explicação simples",
        },
        {
          type: "paragraph",
          text: "Uma blockchain é como um grande livro de registros digital, público e compartilhado.",
        },
        {
          type: "paragraph",
          text: "Imagine uma caderneta onde cada página é um bloco e todas as páginas estão conectadas formando uma cadeia.",
        },
        {
          type: "paragraph",
          text: "Em vez de ficar guardada em um único lugar (como um banco ou empresa), essa caderneta fica distribuída por milhares de computadores — chamados de nodos — espalhados pelo mundo.",
        },
        {
          type: "paragraph",
          text: "Todos têm a mesma cópia, e quando alguém faz uma transação (por exemplo, envia criptomoedas), essa informação vai para a “próxima página” do livro.",
        },
        {
          type: "paragraph",
          text: "Depois de registrada e verificada, ninguém pode apagar ou alterar.",
        },
        {
          type: "paragraph",
          text: "📌 O que pode ser armazenado em uma blockchain?",
        },
        {
          type: "list",
          items: [
            "Transações financeiras (como pagamentos em criptomoedas)",
            "Propriedade de itens digitais (como NFTs ou ingressos digitais)",
            "Contratos automáticos (smart contracts)",
            "Registros de identidade, certificados ou dados médicos",
          ],
        },
      ],
    },
    {
      id: "transaccion",
      navLabel: "Transação",
      title: "🔁 Como funciona uma transação na blockchain?",
      content: [
        "Vamos usar uma história para deixar simples:",
        {
          type: "orderedList",
          items: [
            "Ana quer enviar 10 USDC para Diego.",
            "Ela usa sua wallet (carteira digital) para criar essa transação.",
            "A operação é enviada para a rede blockchain, onde vários computadores a recebem.",
            "Os nodos verificam se Ana tem saldo suficiente e se a assinatura digital é válida.",
            "Se tudo estiver correto, a transação é colocada junto com outras dentro de um novo bloco.",
            "O bloco é validado e incluído na cadeia.",
            "Diego recebe os 10 USDC na wallet dele..",
          ],
        },
        "E o mais importante:",
        "Qualquer pessoa pode ver a transação na blockchain, mas ninguém pode alterá-la.",
        {
          type: "list",
          items: [
            "Observação: USDC é uma moeda digital estável, parecida com um dólar digital.",
          ],
        },
        {
          type: "paragraph",
          text: "🧠 Por que isso é tão seguro?",
        },
        {
          type: "list",
          items: [
            "Cada bloco tem um código único (como se fosse uma impressão digital).",
            "Esse código depende do bloco anterior.",
            "Se alguém tentar mudar qualquer detalhe, o código fica inválido, e a rede rejeita automaticamente.",
          ],
        },
      ],
    },
    {
      id: "descentralizacion",
      navLabel: "Descentralização",
      title: "🤝 O que é descentralização?",
      content: [
        {
          type: "paragraph",
          text: "Descentralizar significa que não existe um único dono ou controlador.",
        },
        {
          type: "paragraph",
          text: "Em vez de estar tudo no servidor de uma empresa, os dados ficam distribuídos entre milhares de pessoas.",
        },
        {
          type: "paragraph",
          text: "🧠 Comparação:",
        },
        {
          type: "list",
          items: [
            "Banco: seus dados ficam nos servidores do banco.",
            "Blockchain: seus dados ficam espalhados pela rede — ninguém controla sozinho e todos se fiscalizam.",
          ],
        },
      ],
    },
    {
      id: "consenso",
      navLabel: "Consenso",
      title: "🔐 O que é consenso?",
      content: [
        {
          type: "paragraph",
          text: "Para registrar uma transação na blockchain, a rede inteira precisa entrar em acordo sobre o que é válido.",
        },
        {
          type: "paragraph",
          text: "Esse processo é chamado de mecanismo de consenso.",
        },
        {
          type: "paragraph",
          text: "🔄 Duas formas comuns de alcançar consenso:",
        },
        {
          type: "table",
          headers: ["Método", "Como funciona?", "Exemplo"],
          rows: [
            [
              "Prova de Trabalho (Proof of Work)",
              "Computadores resolvem desafios matemáticos; quem resolve primeiro valida o bloco",
              "Bitcoin",
            ],
            [
              "Prova de Participação (Proof of Stake)",
              "Quem possui moedas “participa” da validação e ganha por isso",
              "Ethereum (atual), Solana",
            ],
          ],
        },
        {
          type: "paragraph",
          text: "🧠 Curiosidade:",
        },
        {
          type: "paragraph",
          text: "Na Prova de Participação, quanto mais confiança e moedas o validador tem, mais chances ele tem de validar blocos e ganhar recompensas.",
        },
      ],
    },
    {
      id: "ejemplos",
      navLabel: "Exemplos reais",
      title: "🏢 Exemplos reais",
      content: [
        {
          type: "list",
          items: ["IBM + Maersk"],
        },
        {
          type: "paragraph",
          text: "🧠 Curiosidade:",
        },
        {
          type: "list",
          items: ["Visa + Stellar"],
        },
        {
          type: "paragraph",
          text: "Parceria para pagamentos internacionais mais rápidos e baratos usando blockchain.",
        },
        {
          type: "list",
          items: ["Natura (Brasil)"],
        },
        {
          type: "paragraph",
          text: "Estuda blockchain para rastrear cadeia produtiva e comprovar origem sustentável de ingredientes.",
        },
        {
          type: "list",
          items: ["Mercado Livre (LatAm)"],
        },
        {
          type: "paragraph",
          text: "Já vende NFTs colecionáveis e integra soluções Web3 em alguns produtos digitais.",
        },
      ],
    },
    {
      id: "videos",
      navLabel: "Vídeos",
      title: "📺 Vídeos recomendados",
      content: [
        {
          type: "paragraph",
          text: "🎥 O que é Blockchain? – Explicado fácil e visual (Cripto em Minutos) – 5 min",
        },
        {
          type: "paragraph",
          text: "🎥 Como funciona uma transação em blockchain (Binance) – 9 min",
        },
        {
          type: "paragraph",
          text: "🎥 Blockchain em 10 minutos (PT-BR) – diversos canais locais explicam de forma simples",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Leituras complementares",
      title: "📚 Leituras complementares",
      content: [
        {
          type: "paragraph",
          text: "Binance Academy – Como funciona blockchain",
        },
        {
          type: "paragraph",
          text: "CoinTelegraph – Guia para iniciantes sobre blockchain",
        },
        {
          type: "paragraph",
          text: "Artigos e vídeos brasileiros sobre Web3 e cripto (sempre priorize fontes confiáveis)",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Quiz-  O quanto você sabe sobre blockchain?",
      questions: [
        {
          id: "q1",
          question: "O que é uma blockchain?",
          options: [
            { key: "a", text: "Um arquivo salvo em um banco" },
            { key: "b", text: "Uma lista privada de senhas" },
            {
              key: "c",
              text: "Um livro digital compartilhado entre várias máquinas",
            },
            { key: "d", text: "Uma rede social descentralizada" },
          ],
          correct: "c",
        },
        {
          id: "q2",
          question: "O que representa um bloco da cadeia?",
          options: [
            { key: "a", text: "Um vírus" },
            {
              key: "b",
              text: "Um registro validado que não pode ser alterado",
            },
            { key: "c", text: "Uma mensagem temporária" },
            { key: "d", text: "Uma senha secreta" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "O que acontece se alguém tenta alterar um bloco?",
          options: [
            { key: "a", text: "A rede ignora" },
            { key: "b", text: "O bloco desaparece" },
            { key: "c", text: "O código muda e a rede rejeita" },
            { key: "d", text: "Toda a cadeia é apagada" },
          ],
          correct: "c",
        },
        {
          id: "q4",
          question: "O que é um nodo?",
          options: [
            { key: "a", text: "Um hacker" },
            { key: "b", text: "Uma carteira digital" },
            { key: "c", text: "Um computador que valida dados" },
            { key: "d", text: "Um contrato inteligente" },
          ],
          correct: "c",
        },
        {
          id: "q5",
          question: "O que é descentralização?",
          options: [
            { key: "a", text: "Uma empresa controla tudo" },
            { key: "b", text: "O Banco Central distribui os dados" },
            {
              key: "c",
              text: "Os dados ficam espalhados entre várias pessoas",
            },
            { key: "d", text: "O governo supervisiona cada transação" },
          ],
          correct: "c",
        },
        {
          id: "q6",
          question: "Para que serve o consenso?",
          options: [
            { key: "a", text: "Apagar transações" },
            { key: "b", text: "Validar transações" },
            { key: "c", text: "Aumentar o preço das criptos" },
            { key: "d", text: "Conectar a blockchain à internet" },
          ],
          correct: "b",
        },
        {
          id: "q7",
          question: "Como funciona a Prova de Trabalho?",
          options: [
            { key: "a", text: "Sorteio" },
            { key: "b", text: "Computadores resolvem desafios matemáticos" },
            { key: "c", text: "Bancos aprovam manualmente" },
            { key: "d", text: "Quem tem mais amigos ganha" },
          ],
          correct: "b",
        },
        {
          id: "q8",
          question: "Qual método usa o Ethereum hoje?",
          options: [
            { key: "a", text: "Prova de Trabalho" },
            { key: "b", text: "Prova de Votos" },
            { key: "c", text: "Prova de Participação" },
            { key: "d", text: "Prova de Governo" },
          ],
          correct: "c",
        },
        {
          id: "q9",
          question: "O que pode ser registrado na blockchain?",
          options: [
            { key: "a", text: "Somente imagens" },
            { key: "b", text: "Somente senhas" },
            { key: "c", text: "Diversos tipos de dados" },
            { key: "d", text: "Telefones" },
          ],
          correct: "c",
        },
        {
          id: "q10",
          question: "Por que a blockchain é tão segura?",
          options: [
            { key: "a", text: "Porque é privada" },
            { key: "b", text: "Os dados se autodestróem" },
            {
              key: "c",
              text: "Os blocos são conectados e qualquer mudança quebra a cadeia",
            },
            { key: "d", text: "Só funciona com senha" },
          ],
          correct: "c",
        },
      ],
    },
    {
      id: "frase",
      navLabel: "Frase do dia",
      title: "🧠 Frase do dia",
      content: [
        "“Blockchain não é só tecnologia. É confiança construída por milhares de pessoas.”",
      ],
    },
  ],
};

export default modulo2_pt;
